// ============================================================
// NewCaseModal.tsx — "+ New case" (case-entry-modals plan, step 2).
//
// Built on the shared ModalFrame (size md) to the approved mockup
// (casevo-case-entry-modals.html, screens 1 + 1b). One modal, two client
// paths, ALWAYS ending in a new case for that client:
//   • NEW CLIENT       — the create-client form (ported from
//                        ClientPickerModal create mode: same fields, same
//                        required forename / surname / main contact number,
//                        same AddressLookup + manual lines, same DOB
//                        "not the accident date" hint) → createClient.
//   • EXISTING CLIENT  — today's "Open Existing" flow, restyled (ported from
//                        ClientPickerModal pick mode: debounced server
//                        search, A–Z / Z–A / Recently used, ↑/↓ + Enter,
//                        inline delete-with-confirm, error + Retry).
// Then createCaseApi({ clientId, claimType, claimRoute }) — the claim type is
// REQUIRED (B2: ClaimTypeFields, beside the accident date; picking one
// pre-fills its usual route) — and, if any were filled, the OPTIONAL
// case fields (case name / accident date / defendant) are saved straight
// after via the existing updateCaseApi, exactly like the quick-view's edit
// (CaseQuickViewModal.tsx:257-263). No schema change.
//
// Retry-safe: once the client exists its id is kept, so a failed case
// create retried never makes a duplicate client.
//
// ClientPickerModal itself is untouched (Import + Tasks still use it).
// ============================================================
import { refusalText } from '../../utils/serverError'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ModalFrame from '../shared/ModalFrame'
import AddressLookup from '../shared/AddressLookup'
import DateInput from '../shared/DateInput'
import { accidentDateError, accidentDateWarning, dobError, dobWarning } from '../../utils/caseDateRules'
import { londonToday } from '../../utils/civilDate'
import { useCasevoStore } from '../../store/useCasevoStore'
import { canDeleteClients } from '../../utils/casePermissions'
import { RESTRICTED_NAME_WARNING } from '../../utils/restrictedName'
import { checkRestrictedClientNameApi, createCaseApi, updateCaseApi } from '../../api/casevoApi'
import { serverErrorMessage } from '../../utils/serverError'
import { BTN_PRIMARY } from '../../styles/buttons'
import { useDraftDirty, useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard'
import type { Client, CreateClientPayload } from '../../types/client.types'
import { BTN_CANCEL, FIELD, FIELD_ERR, HINT, LBL, REQ, SEC, SMALL } from './caseEntryStyles'
import ClaimTypeFields, { CLAIM_TYPE_MISSING, EMPTY_CLAIM_CHOICE, type ClaimChoice } from './ClaimTypeFields'
import { refusedField } from '../../hooks/fieldRefusals'
import FieldError from '../shared/FieldError'
import { emailFormatError, phoneFormatError } from '../../utils/contactFormat'

export type NewCaseTab = 'new' | 'existing'
export { RESTRICTED_NAME_WARNING }
const RestrictedNameNote: React.FC = () => <p role="status" data-restricted-name className="mt-0 mb-3 text-[13px] leading-[1.45] text-casevo-warn">{RESTRICTED_NAME_WARNING}</p>

interface NewCaseModalProps {
  isOpen: boolean
  /** Which client path opens first ("+ New" → 'new'; the header's Open
   *  Existing button → 'existing' until step 3 replaces it). */
  initialTab?: NewCaseTab
  onClose: () => void
  /** The case exists. `warning` is set when the optional case fields
   *  couldn't be saved (the case is still created + opened). */
  onCreated: (caseId: string, warning?: string) => void
}

const TITLE_OPTIONS = ['Mr', 'Mrs', 'Miss', 'Ms', 'Dr', 'Prof'] as const
const SEARCH_DEBOUNCE_MS = 300

// Mockup values (casevo-case-entry-modals.html) — shared: ./caseEntryStyles.

const Ic: React.FC<{ d: React.ReactNode; className?: string }> = ({ d, className = 'w-[14px] h-[14px]' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={className}>{d}</svg>
)
const IC_PLUS = <><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></>
const IC_USERS = <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>
const IC_USERPLUS = <><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="8.5" cy="7" r="4" /><line x1="20" y1="8" x2="20" y2="14" /><line x1="23" y1="11" x2="17" y2="11" /></>
const IC_SEARCH = <><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></>
const IC_TRASH = <><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /></>
const IC_CHECK = <polyline points="20 6 9 17 4 12" />

const clientLabel = (c: Client): string => [c.title, c.forename, c.surname].filter(Boolean).join(' ')
const rowLabel = (c: Client): string => `${clientLabel(c)} — Client #${c.clientNumber}`
const clientSub = (c: Client): string => {
  const place = [c.addressTown, c.addressPostcode ? c.addressPostcode.split(' ')[0] : ''].filter(Boolean).join(' ')
  return [`Client #${c.clientNumber}`, place].filter(Boolean).join(' · ')
}
const initials = (c: Client): string => `${c.forename?.[0] ?? ''}${c.surname?.[0] ?? ''}`.toUpperCase() || '?'

const NewCaseModal: React.FC<NewCaseModalProps> = ({ isOpen, initialTab = 'new', onClose, onCreated }) => {
  // (Audit batch 3, H4) A search can return walled clients as bare
  // "Restricted client" stubs — a picker can't use them, so they're left out.
  const allClients = useCasevoStore((s) => s.clients)
  const clients = useMemo(() => allClients.filter((c) => !c.restricted), [allClients])
  const restrictedInSearch = allClients.some((c) => c.restricted)
  const authUser = useCasevoStore((s) => s.authUser)
  const canDelete = canDeleteClients(authUser)
  const clientsLoading = useCasevoStore((s) => s.clientsLoading)
  const clientsError = useCasevoStore((s) => s.clientsError)
  const loadClients = useCasevoStore((s) => s.loadClients)
  const createClient = useCasevoStore((s) => s.createClient)
  const deleteClient = useCasevoStore((s) => s.deleteClient)

  const [tab, setTab] = useState<NewCaseTab>(initialTab)

  // ── new-client form ──
  const [fTitle, setFTitle] = useState('')
  const [fForename, setFForename] = useState('')
  const [fSurname, setFSurname] = useState('')
  const [fPhone, setFPhone] = useState('')
  const [fEmail, setFEmail] = useState('')
  const [fDob, setFDob] = useState('')
  const [fLine1, setFLine1] = useState('')
  const [fLine2, setFLine2] = useState('')
  const [fTown, setFTown] = useState('')
  const [fCounty, setFCounty] = useState('')
  const [fPostcode, setFPostcode] = useState('')
  const [manualAddress, setManualAddress] = useState(false)
  const [triedSubmit, setTriedSubmit] = useState(false)
  // A real phone / email (utils/contactFormat) — said once the field is left.
  const [leftContact, setLeftContact] = useState<{ phone?: boolean; email?: boolean }>({})

  // ── optional case fields ──
  const [cName, setCName] = useState('')
  const [cAccident, setCAccident] = useState<string | null>(null)
  // DATE HARDENING: Create waits while a date is half-typed (DateInput
  // onValidityChange) or breaks a rule the server would refuse.
  const [accTyping, setAccTyping] = useState(false)
  const [dobTyping, setDobTyping] = useState(false)
  const [cDefendant, setCDefendant] = useState('')

  // ── existing-client picker ──
  const [search, setSearch] = useState('')
  const [sortMode, setSortMode] = useState<'az' | 'za' | 'recent'>('az')
  const [highlighted, setHighlighted] = useState<number | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  // ── submit ──
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Unsaved-changes guard: ×, Esc, backdrop, Cancel (and leaving the screen /
  // Back / refresh) ask before dropping what was typed here.
  const guardClose = useUnsavedChangesGuard(useDraftDirty(isOpen, [fTitle, fForename, fSurname, fPhone, fEmail, fDob, fLine1, fLine2, fTown, fCounty, fPostcode, cName, cAccident, cDefendant, selectedId]) && !busy)
  const requestClose = (): void => guardClose(onClose)
  // A client created by THIS modal session — reused on retry so a failed
  // case-create never produces a duplicate client.
  const createdClientIdRef = useRef<string | null>(null)
  // The claim type (required) + route (B2).
  const [claim, setClaim] = useState<ClaimChoice>(EMPTY_CLAIM_CHOICE)
  const [claimError, setClaimError] = useState<string | null>(null)

  const forenameRef = useRef<HTMLInputElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const rowRefs = useRef<(HTMLLIElement | null)[]>([])
  const firstSearchRun = useRef(true)

  // Reset everything on open (ClientPickerModal.tsx:118-146 parity) + load.
  useEffect(() => {
    if (!isOpen) return
    setTab(initialTab)
    setFTitle(''); setFForename(''); setFSurname(''); setFPhone(''); setFEmail(''); setFDob('')
    setFLine1(''); setFLine2(''); setFTown(''); setFCounty(''); setFPostcode(''); setManualAddress(false)
    setTriedSubmit(false)
    setCName(''); setCAccident(null); setCDefendant('')
    setClaim(EMPTY_CLAIM_CHOICE); setClaimError(null)
    setSearch(''); setSortMode('az'); setHighlighted(null); setSelectedId(null)
    setConfirmDeleteId(null); setDeletingId(null); setDeleteError(null)
    setBusy(false); setError(null)
    createdClientIdRef.current = null
    firstSearchRun.current = true
    void loadClients()
  }, [isOpen, initialTab, loadClients])

  // Focus the first field of whichever path is showing.
  useEffect(() => {
    if (!isOpen) return
    const id = requestAnimationFrame(() => {
      if (tab === 'new') forenameRef.current?.focus()
      else searchRef.current?.focus()
    })
    return () => cancelAnimationFrame(id)
  }, [isOpen, tab])

  // Debounced server search (+ 'recent' sort is server-side) — skips the
  // first run because the open-effect already loaded (ClientPickerModal parity).
  useEffect(() => {
    if (!isOpen) return
    if (firstSearchRun.current) { firstSearchRun.current = false; return }
    const t = setTimeout(() => {
      void loadClients(search.trim() || undefined, sortMode === 'recent' ? 'recent' : undefined)
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [search, sortMode, isOpen, loadClients])

  // (Big-audit batch 4, item 11) The typed name matches a client this user can't
  // see. The server's bare yes / no; a failed check simply shows nothing.
  const [restrictedName, setRestrictedName] = useState(false)
  useEffect(() => {
    setRestrictedName(false)
    const forename = fForename.trim(); const surname = fSurname.trim()
    if (!isOpen || tab !== 'new' || !forename || !surname) return
    let stale = false
    const t = setTimeout(() => {
      checkRestrictedClientNameApi(forename, surname).then((yes) => { if (!stale) setRestrictedName(yes) }).catch(() => {})
    }, SEARCH_DEBOUNCE_MS)
    return () => { stale = true; clearTimeout(t) }
  }, [fForename, fSurname, isOpen, tab])

  useEffect(() => { setConfirmDeleteId(null); setDeleteError(null) }, [search])
  useEffect(() => { setHighlighted(null) }, [isOpen, search, sortMode, tab])

  const sortedClients = useMemo(() => {
    if (sortMode === 'recent') return clients
    const key = (c: Client): string => `${c.surname} ${c.forename}`.toLowerCase()
    const sorted = [...clients].sort((a, b) => key(a).localeCompare(key(b), undefined, { numeric: true, sensitivity: 'base' }))
    if (sortMode === 'za') sorted.reverse()
    return sorted
  }, [clients, sortMode])

  // Drop a selection that's no longer in the list (deleted / filtered away).
  useEffect(() => {
    if (selectedId && !clients.some((c) => c.id === selectedId)) setSelectedId(null)
  }, [clients, selectedId])
  useEffect(() => {
    if (highlighted !== null && highlighted >= sortedClients.length) {
      setHighlighted(sortedClients.length > 0 ? sortedClients.length - 1 : null)
    }
  }, [sortedClients.length, highlighted])
  useEffect(() => {
    if (highlighted === null) return
    rowRefs.current[highlighted]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }, [highlighted])

  const phoneErr = phoneFormatError(fPhone)
  const emailErr = emailFormatError(fEmail)
  const newClientValid = !!(fForename.trim() && fSurname.trim() && fPhone.trim()) && !phoneErr && !emailErr
  const today = londonToday()
  const dobBlock = tab === 'new' ? dobError(fDob || null, today) : null
  // Existing tab: the accident is checked against the PICKED client's DOB
  // (the server refuses an accident before it).
  const dobOfClient = (id: string | null): string | null => (id ? clients.find((c) => c.id === id)?.dateOfBirth ?? null : null)
  const accBlock = accidentDateError(cAccident, tab === 'new' ? fDob || null : dobOfClient(selectedId), today)
  const datesOk = !accTyping && !accBlock && !(tab === 'new' && (dobTyping || dobBlock))
  const canCreate = !busy && datesOk && (tab === 'new' ? newClientValid : !!selectedId)

  // ── create flow ─────────────────────────────────────────────
  const createCaseFor = useCallback(async (clientId: string): Promise<void> => {
    if (!claim.claimType) throw new Error(CLAIM_TYPE_MISSING)
    const created = await createCaseApi({ clientId, claimType: claim.claimType, claimRoute: claim.claimRoute || null })
    const patch: Record<string, unknown> = {}
    if (cName.trim()) patch.caseName = cName.trim()
    if (cDefendant.trim()) patch.defendantName = cDefendant.trim()
    const problems: string[] = []
    if (Object.keys(patch).length > 0) {
      try {
        await updateCaseApi(created.id, patch)
      } catch (err) {
        const why = refusalText(err, '')
        problems.push(why ? `its name / defendant wasn’t saved (${why.replace(/\.$/, '')})` : 'its name / defendant couldn’t be saved')
      }
    }
    // ★ The accident date goes in its OWN PUT: the server can refuse it
    // (e.g. before the client's date of birth), and in the shared PUT that
    // lost the case name and defendant too.
    if (cAccident) {
      try {
        await updateCaseApi(created.id, { accidentDate: cAccident })
      } catch (err) {
        const why = serverErrorMessage(err, [400, 422])
        problems.push(why ? `its accident date wasn’t saved (${why.replace(/\.$/, '')})` : 'its accident date couldn’t be saved')
      }
    }
    const warning = problems.length > 0 ? `The case was created, but ${problems.join(' and ')} — please add ${problems.length > 1 ? 'them' : 'it'} on the case.` : undefined
    onCreated(created.id, warning)
  }, [claim, cName, cAccident, cDefendant, onCreated])

  const handleCreate = async (pickedId?: string): Promise<void> => {
    if (busy) return
    setError(null)
    // The claim type is required whichever client path (FieldError under it).
    const claimMissing = !claim.claimType
    setClaimError(claimMissing ? CLAIM_TYPE_MISSING : null)
    try {
      if (tab === 'existing' || pickedId) {
        const id = pickedId ?? selectedId
        if (!id || claimMissing) return
        // Double-click creates straight away — run the same date check.
        const block = accidentDateError(cAccident, dobOfClient(id), today)
        if (block) { setError(block); return }
        setBusy(true)
        await createCaseFor(id)
        return
      }
      // NEW CLIENT — same validation + payload as ClientPickerModal.tsx:369-400.
      if (!newClientValid) {
        setTriedSubmit(true)
        setLeftContact({ phone: true, email: true })
        setError(phoneErr || emailErr ? 'Check the main contact number and email.' : 'Forename, surname, and main contact number are required.')
        return
      }
      if (claimMissing) return
      setBusy(true)
      let clientId = createdClientIdRef.current
      if (!clientId) {
        const payload: CreateClientPayload = {
          title: fTitle || null,
          forename: fForename.trim(),
          surname: fSurname.trim(),
          addressLine1: fLine1.trim(),
          addressLine2: fLine2.trim(),
          addressTown: fTown.trim(),
          addressCounty: fCounty.trim(),
          addressPostcode: fPostcode.trim(),
          dateOfBirth: fDob || null,
          hasNiNumber: null,
          niNumber: '',
          mainContactNumber: fPhone.trim(),
          emailAddress: fEmail.trim(),
          employmentStatus: null,
          occupation: '',
        }
        const newClient = await createClient(payload)
        clientId = newClient.id
        createdClientIdRef.current = clientId
      }
      await createCaseFor(clientId)
    } catch (err) {
      // The server refused the claim type → under that field.
      const refused = refusedField(err)
      if (refused?.field === 'claimType' || refused?.field === 'claimRoute') {
        setClaimError(refused.message)
        setBusy(false)
        return
      }
      const msg = err instanceof Error ? err.message : ''
      setError(
        createdClientIdRef.current || tab === 'existing'
          ? 'Failed to create the case. Please try again.'
          : msg || 'Failed to create client',
      )
      setBusy(false)
    }
  }

  // ── existing-client keyboard (↑/↓ + Enter) — ClientPickerModal.tsx:203-246 ──
  const onListKey = (e: React.KeyboardEvent): void => {
    if (tab !== 'existing') return
    const target = e.target as HTMLElement
    const isSearch = target === searchRef.current
    if (!isSearch && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT')) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlighted((h) => (sortedClients.length === 0 ? null : h === null ? 0 : Math.min(h + 1, sortedClients.length - 1)))
    } else if (e.key === 'ArrowUp') {
      if (highlighted === null) return
      e.preventDefault()
      setHighlighted(Math.max(highlighted - 1, 0))
    } else if (e.key === 'Enter' && isSearch) {
      if (highlighted === null) return
      const c = sortedClients[highlighted]
      if (!c || confirmDeleteId === c.id) return
      e.preventDefault()
      // First Enter selects; Enter on the already-selected client creates.
      if (selectedId === c.id) void handleCreate(c.id)
      else setSelectedId(c.id)
    }
  }

  const handleConfirmDelete = async (c: Client): Promise<void> => {
    if (deletingId === c.id) return
    setDeletingId(c.id)
    setDeleteError(null)
    try {
      await deleteClient(c.id)
      setConfirmDeleteId(null)
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete client')
    } finally {
      setDeletingId(null)
    }
  }

  const hasAddress = !!(fLine1 || fLine2 || fTown || fCounty || fPostcode)
  const showManual = manualAddress || hasAddress
  const reqCls = (v: string): string => (triedSubmit && !v.trim() ? FIELD_ERR : FIELD)

  const segBtn = (on: boolean, extra = ''): string =>
    `inline-flex items-center gap-[7px] text-[13px] px-[14px] py-[8px] transition-colors focus:outline-none casevo-focus ${extra} ${
      on ? 'bg-casevo-primary text-white font-semibold' : 'text-casevo-mid hover:text-casevo-forest hover:bg-casevo-input'
    }`
  const sortBtn = (on: boolean, extra = ''): string =>
    `text-[12px] px-[10px] py-[7px] transition-colors focus:outline-none casevo-focus ${extra} ${
      on ? 'bg-casevo-mint text-casevo-brand dark:text-[#8fd6b0] font-semibold' : 'text-casevo-mid hover:text-casevo-forest'
    }`

  const caseFields = (
    <div>
      <div className={SEC}>
        Case details <span className="normal-case tracking-normal font-medium text-[11.5px]">claim type required · the rest can be filled in later</span>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 mb-3">
        <ClaimTypeFields
          idPrefix="nc"
          value={claim}
          onChange={(next) => { setClaim(next); if (next.claimType) setClaimError(null) }}
          error={claimError}
          disabled={busy}
        />
        <div>
          <span className={LBL}>Accident date</span>
          <DateInput value={cAccident} onChange={setCAccident} onValidityChange={(ok) => setAccTyping(!ok)} ariaLabel="Accident date" fieldClassName="border-casevo-border rounded-lg py-2.5 dark:bg-[#1f2e26]" />
          {accBlock ? <p className="text-xs text-casevo-danger mt-1" role="alert">{accBlock}</p>
            : accidentDateWarning(cAccident, today) && <p className="text-xs text-casevo-warn mt-1" role="status">{accidentDateWarning(cAccident, today)}</p>}
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className={LBL} htmlFor="nc-case-name">Case name<small className={SMALL}>optional</small></label>
          <input id="nc-case-name" className={FIELD} value={cName} onChange={(e) => setCName(e.target.value)} placeholder="e.g. Hughes v Ardent Logistics" />
        </div>
        <div>
          <label className={LBL} htmlFor="nc-defendant">Defendant<small className={SMALL}>optional</small></label>
          <input id="nc-defendant" className={FIELD} value={cDefendant} onChange={(e) => setCDefendant(e.target.value)} placeholder="e.g. Ardent Logistics Ltd" />
        </div>
      </div>
      <p className={HINT}>Leave the name blank to use the client’s name. The accident date and claim type drive limitation.</p>
    </div>
  )

  const newClientForm = (
    <div className="mb-[22px]">
      <div className="grid gap-3 grid-cols-1 sm:grid-cols-[110px_1fr_1fr] mb-3">
        <div>
          <label className={LBL} htmlFor="nc-title">Title</label>
          <select id="nc-title" className={FIELD} value={fTitle} onChange={(e) => setFTitle(e.target.value)}>
            <option value="">—</option>
            {TITLE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className={LBL} htmlFor="nc-forename">Forename{REQ}</label>
          <input id="nc-forename" ref={forenameRef} className={reqCls(fForename)} value={fForename} onChange={(e) => setFForename(e.target.value)} required aria-required="true" />
        </div>
        <div>
          <label className={LBL} htmlFor="nc-surname">Surname{REQ}</label>
          <input id="nc-surname" className={reqCls(fSurname)} value={fSurname} onChange={(e) => setFSurname(e.target.value)} required aria-required="true" />
        </div>
      </div>
      {restrictedName && <RestrictedNameNote />}
      <div className="grid gap-3 sm:grid-cols-2 mb-3">
        <div>
          <label className={LBL} htmlFor="nc-phone">Main contact number{REQ}</label>
          <input id="nc-phone" type="tel" className={leftContact.phone && phoneErr ? FIELD_ERR : reqCls(fPhone)} value={fPhone} onChange={(e) => setFPhone(e.target.value)} onBlur={() => setLeftContact((l) => ({ ...l, phone: true }))} required aria-required="true" aria-invalid={leftContact.phone && !!phoneErr ? true : undefined} aria-describedby={leftContact.phone && phoneErr ? 'nc-phone-err' : undefined} />
          <span id="nc-phone-err"><FieldError message={leftContact.phone ? phoneErr ?? undefined : undefined} /></span>
        </div>
        <div>
          <label className={LBL} htmlFor="nc-email">Email<small className={SMALL}>optional</small></label>
          <input id="nc-email" type="email" className={leftContact.email && emailErr ? FIELD_ERR : FIELD} value={fEmail} onChange={(e) => setFEmail(e.target.value)} onBlur={() => setLeftContact((l) => ({ ...l, email: true }))} aria-invalid={leftContact.email && !!emailErr ? true : undefined} aria-describedby={leftContact.email && emailErr ? 'nc-email-err' : undefined} />
          <span id="nc-email-err"><FieldError message={leftContact.email ? emailErr ?? undefined : undefined} /></span>
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <span className={LBL}>Client’s date of birth<small className={SMALL}>not the accident date</small></span>
          <DateInput value={fDob || null} onChange={(v) => setFDob(v || '')} onValidityChange={(ok) => setDobTyping(!ok)} ariaLabel="Client's date of birth" fieldClassName="border-casevo-border rounded-lg py-2.5 dark:bg-[#1f2e26]" />
          {dobBlock ? <p className="text-xs text-casevo-danger mt-1" role="alert">{dobBlock}</p>
            : dobWarning(fDob || null, today) && <p className="text-xs text-casevo-warn mt-1" role="status">{dobWarning(fDob || null, today)}</p>}
          <p className={HINT}>DD / MM / YYYY · the accident date is added below.</p>
        </div>
        <div>
          <label className={LBL} htmlFor="nc-address">Address<small className={SMALL}>postcode lookup</small></label>
          <AddressLookup
            variant="info"
            inputId="nc-address"
            value={hasAddress ? { line1: fLine1, line2: fLine2, town: fTown, county: fCounty, postcode: fPostcode } : null}
            onSelect={(a) => { setFLine1(a.line1); setFLine2(a.line2); setFTown(a.town); setFCounty(a.county); setFPostcode(a.postcode) }}
            onClear={() => { setFLine1(''); setFLine2(''); setFTown(''); setFCounty(''); setFPostcode('') }}
          />
          {!showManual && (
            <button type="button" onClick={() => setManualAddress(true)} className="mt-[5px] text-[11.5px] font-semibold text-casevo-brand hover:underline focus:outline-none casevo-focus rounded">
              Enter manually
            </button>
          )}
        </div>
      </div>
      {showManual && (
        <div className="grid gap-3 sm:grid-cols-2 mt-3">
          <input aria-label="Address line 1" placeholder="Address line 1" className={FIELD} value={fLine1} onChange={(e) => setFLine1(e.target.value)} />
          <input aria-label="Address line 2" placeholder="Address line 2" className={FIELD} value={fLine2} onChange={(e) => setFLine2(e.target.value)} />
          <input aria-label="Town" placeholder="Town" className={FIELD} value={fTown} onChange={(e) => setFTown(e.target.value)} />
          <div className="grid grid-cols-2 gap-3">
            <input aria-label="County" placeholder="County" className={FIELD} value={fCounty} onChange={(e) => setFCounty(e.target.value)} />
            <input aria-label="Postcode" placeholder="Postcode" className={FIELD} value={fPostcode} onChange={(e) => setFPostcode(e.target.value.toUpperCase())} />
          </div>
        </div>
      )}
    </div>
  )

  const existingClientPicker = (
    <div className="mb-[22px]" onKeyDown={onListKey}>
      {search.trim() && restrictedInSearch && <RestrictedNameNote />}
      <div className="flex items-center gap-[10px] mb-[10px] flex-wrap sm:flex-nowrap">
        <div className="relative flex-1 min-w-[200px]">
          <Ic d={IC_SEARCH} className="w-[15px] h-[15px] absolute left-3 top-1/2 -translate-y-1/2 text-casevo-text-soft pointer-events-none" />
          <input
            ref={searchRef}
            type="text"
            aria-label="Search clients by name"
            placeholder="Search clients by name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={`${FIELD} pl-[34px]`}
            aria-activedescendant={highlighted !== null && sortedClients[highlighted] ? `nc-client-${sortedClients[highlighted].id}` : undefined}
            aria-controls="nc-client-list"
          />
        </div>
        <div className="inline-flex border border-casevo-border rounded-lg overflow-hidden shrink-0" role="group" aria-label="Sort clients">
          <button type="button" aria-pressed={sortMode === 'az'} onClick={() => setSortMode('az')} className={sortBtn(sortMode === 'az')}>A–Z</button>
          <button type="button" aria-pressed={sortMode === 'za'} onClick={() => setSortMode('za')} className={sortBtn(sortMode === 'za', 'border-l border-casevo-border')}>Z–A</button>
          <button type="button" aria-pressed={sortMode === 'recent'} onClick={() => setSortMode('recent')} className={sortBtn(sortMode === 'recent', 'border-l border-casevo-border')}>Recently used</button>
        </div>
      </div>

      {clientsError && (
        <div className="mb-[10px] px-3 py-2 rounded-lg text-xs flex items-center justify-between gap-2 bg-[#fbe9e6] text-[#c23b2c] dark:bg-[rgba(243,160,148,0.14)] dark:text-[#f3a094]">
          <span>{clientsError}</span>
          <button type="button" className="underline whitespace-nowrap" onClick={() => void loadClients(search.trim() || undefined, sortMode === 'recent' ? 'recent' : undefined)}>Retry</button>
        </div>
      )}

      <div className="border border-casevo-border rounded-[12px] overflow-hidden">
        {clientsLoading ? (
          <ul aria-hidden="true">
            {[0, 1, 2].map((i) => (
              <li key={i} className="flex items-center gap-[12px] px-[14px] py-[11px] border-t border-casevo-border-soft first:border-t-0 animate-pulse">
                <span className="w-[28px] h-[28px] rounded-full bg-casevo-mint/60" />
                <span className="flex-1"><span className="block w-40 h-[12px] rounded bg-casevo-mint/50 mb-[6px]" /><span className="block w-24 h-[10px] rounded bg-casevo-mint/40" /></span>
              </li>
            ))}
          </ul>
        ) : sortedClients.length === 0 ? (
          <p className="text-sm text-casevo-text-soft py-6 text-center">
            {search.trim() ? `No clients match “${search.trim()}”.` : 'No clients yet.'}{' '}
            <button type="button" onClick={() => setTab('new')} className="font-semibold text-casevo-brand hover:underline focus:outline-none casevo-focus rounded">Add a new client</button>
          </p>
        ) : (
          <ul id="nc-client-list" role="listbox" aria-label="Clients" className="max-h-[288px] overflow-y-auto">
            {sortedClients.map((c, index) => {
              const isSel = selectedId === c.id
              const isHi = highlighted === index
              if (confirmDeleteId === c.id) {
                return (
                  <li key={c.id} ref={(el) => { rowRefs.current[index] = el }} role="alertdialog" aria-label={`Confirm delete ${rowLabel(c)}`}
                    className="flex items-center gap-[12px] px-[14px] py-[11px] border-t border-casevo-border-soft first:border-t-0 bg-[#fbe9e6] dark:bg-[rgba(243,160,148,0.14)]">
                    <span className="w-[28px] h-[28px] rounded-full grid place-items-center text-[11px] font-bold text-white bg-casevo-primary shrink-0">{initials(c)}</span>
                    <div className="flex-1 min-w-0 text-[13px] text-casevo-forest">
                      Delete <b>{rowLabel(c)}</b>? This can’t be undone.
                      {deleteError && <div className="text-xs text-casevo-danger mt-1">{deleteError}</div>}
                    </div>
                    <button type="button" disabled={deletingId === c.id} onClick={() => { setConfirmDeleteId(null); setDeleteError(null) }}
                      className="text-[12px] font-semibold rounded-[7px] px-[11px] py-[5px] border border-casevo-border bg-casevo-surface text-casevo-mid focus:outline-none casevo-focus disabled:opacity-60">Cancel</button>
                    <button type="button" disabled={deletingId === c.id} onClick={() => void handleConfirmDelete(c)}
                      className="text-[12px] font-semibold rounded-[7px] px-[11px] py-[5px] bg-casevo-danger text-white dark:text-[#2a0f0b] focus:outline-none casevo-focus disabled:opacity-60">
                      {deletingId === c.id ? 'Deleting…' : 'Delete'}
                    </button>
                  </li>
                )
              }
              return (
                <li key={c.id} id={`nc-client-${c.id}`} ref={(el) => { rowRefs.current[index] = el }} role="option" aria-selected={isSel}
                  className={`group relative flex items-center border-t border-casevo-border-soft first:border-t-0 transition-colors ${
                    isSel ? 'bg-casevo-input shadow-[inset_3px_0_0_var(--casevo-brand)]' : isHi ? 'bg-casevo-input' : 'hover:bg-casevo-input'
                  }`}>
                  <button type="button" onClick={() => setSelectedId(c.id)} onDoubleClick={() => void handleCreate(c.id)}
                    className="flex-1 min-w-0 flex items-center gap-[12px] pl-[14px] py-[11px] text-left focus:outline-none casevo-focus">
                    <span className="w-[28px] h-[28px] rounded-full grid place-items-center text-[11px] font-bold text-white bg-casevo-primary shrink-0">{initials(c)}</span>
                    <span className="min-w-0">
                      <b className="block text-[13.5px] text-casevo-forest truncate">{clientLabel(c)}</b>
                      <span className="block text-[12.5px] text-casevo-text-soft truncate">{clientSub(c)}</span>
                    </span>
                  </button>
                  <span className="ml-auto pr-[14px] flex items-center gap-[14px] text-[12px] text-casevo-text-soft">
                    {isSel ? (
                      <span className="inline-flex items-center gap-1 font-semibold text-casevo-brand dark:text-[#8fd6b0]"><Ic d={IC_CHECK} className="w-[13px] h-[13px]" />Selected</span>
                    ) : isHi ? (
                      <span>Enter ↵</span>
                    ) : null}
                    {/* (Audit batch 3) Only for "Delete clients" holders / admins. */}
                    {canDelete && (
                    <button type="button" onClick={() => { setConfirmDeleteId(c.id); setDeleteError(null) }} aria-label={`Delete ${rowLabel(c)}`}
                      className="w-[28px] h-[28px] rounded-[7px] grid place-items-center text-casevo-text-soft hover:text-casevo-danger hover:bg-[#fbe9e6] dark:hover:bg-[rgba(243,160,148,0.14)] focus:outline-none casevo-focus">
                      <Ic d={IC_TRASH} className="w-[15px] h-[15px]" />
                    </button>
                    )}
                  </span>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )

  return (
    <ModalFrame
      isOpen={isOpen}
      onClose={requestClose}
      size="md"
      dismissible={!busy}
      eyebrow="New case"
      eyebrowIcon={<Ic d={IC_PLUS} />}
      title="New case"
      subtitle="Choose or add the client, then create the case."
      footerLeft={
        tab === 'new'
          ? <span className="text-[12px] text-casevo-text-soft"><b className="text-casevo-danger">*</b> required</span>
          : <span className="text-[12px] text-casevo-text-soft">{selectedId ? <>Client selected · <b className="text-casevo-danger">*</b> required</> : 'Pick a client to continue'}</span>
      }
      footerRight={
        <>
          <button type="button" onClick={requestClose} disabled={busy} className={BTN_CANCEL}>Cancel</button>
          <button type="button" onClick={() => void handleCreate()} disabled={!canCreate}
            className={`${BTN_PRIMARY} text-sm rounded-lg px-5 py-2.5`} aria-busy={busy}>
            <Ic d={IC_PLUS} className="w-[15px] h-[15px]" />
            {busy ? 'Creating…' : 'Create case'}
          </button>
        </>
      }
    >
      <div className={SEC}>Client</div>
      <div className="inline-flex border border-casevo-border rounded-[9px] overflow-hidden mb-[14px]" role="tablist" aria-label="Client">
        <button type="button" role="tab" aria-selected={tab === 'existing'} onClick={() => { setTab('existing'); setError(null) }} className={segBtn(tab === 'existing')}>
          <Ic d={IC_USERS} />Existing client
        </button>
        <button type="button" role="tab" aria-selected={tab === 'new'} onClick={() => { setTab('new'); setError(null) }} className={segBtn(tab === 'new', 'border-l border-casevo-border')}>
          <Ic d={IC_USERPLUS} />New client
        </button>
      </div>

      {tab === 'new' ? newClientForm : existingClientPicker}

      {caseFields}

      {error && (
        <div role="alert" className="mt-4 px-3 py-2 rounded-lg text-xs bg-[#fbe9e6] text-[#c23b2c] dark:bg-[rgba(243,160,148,0.14)] dark:text-[#f3a094]">{error}</div>
      )}
    </ModalFrame>
  )
}

export default NewCaseModal
